import { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../../contexts/DataContext';

const PHONE = '56942699675';
const ANON_MESSAGE = 'Hola, vengo del sitio de ProveMiner y quiero consultarles algo';

function buildMessage(user, myBusinesses) {
    if (!user) return ANON_MESSAGE;
    const name = (user.fullName || user.full_name || user.name
        || [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
        || '').trim();
    const email = (user.email || '').trim();
    const business = Array.isArray(myBusinesses) && myBusinesses.length > 0
        ? (myBusinesses[0].name || myBusinesses[0].business_name || '').trim()
        : '';

    if (!name && !email) return ANON_MESSAGE;
    const who = name && email ? `${name} (${email})` : (name || email);
    const of = business ? ` de ${business}` : '';
    return `Hola, soy ${who}${of}, vengo del sitio de ProveMiner. Quiero consultarles algo.`;
}

const STORAGE_KEY_CORNER = 'proveminer_wa_fab_corner';
const VALID_CORNERS = ['br', 'bl', 'tr', 'tl'];
const DRAG_THRESHOLD_PX = 6;
const CLICK_MAX_DURATION_MS = 350;
const FAB_SIZE_DESKTOP = 64;
const FAB_SIZE_MOBILE = 48;
const MARGIN_X = 20;
const MARGIN_TOP = 80;
const MARGIN_BOTTOM_DESKTOP = 20;
const MARGIN_BOTTOM_MOBILE = 88;

function readCorner() {
    try {
        const v = localStorage.getItem(STORAGE_KEY_CORNER);
        return VALID_CORNERS.includes(v) ? v : 'br';
    } catch { return 'br'; }
}

function writeCorner(c) {
    try { localStorage.setItem(STORAGE_KEY_CORNER, c); } catch { /* noop */ }
}

function nearestCorner(x, y) {
    if (typeof window === 'undefined') return 'br';
    const w = window.innerWidth, h = window.innerHeight;
    return (y < h / 2 ? 't' : 'b') + (x < w / 2 ? 'l' : 'r');
}

export default function WhatsAppFab() {
    const { user, myBusinesses } = useData();

    const waUrl = useMemo(() => {
        const msg = buildMessage(user, myBusinesses);
        return `https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`;
    }, [user, myBusinesses]);

    const waUrlRef = useRef(waUrl);
    useEffect(() => { waUrlRef.current = waUrl; }, [waUrl]);

    const handleOpen = () => {
        window.open(waUrlRef.current, '_blank', 'noopener,noreferrer');
    };

    const [hidden, setHidden] = useState(false);
    const lastY = useRef(0);
    useEffect(() => {
        lastY.current = window.scrollY || 0;
        const onScroll = () => {
            const y = window.scrollY || 0;
            const delta = y - lastY.current;
            if (Math.abs(delta) < 6) return;
            if (y < 80) setHidden(false);
            else if (delta > 0) setHidden(true);
            else setHidden(false);
            lastY.current = y;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const [corner, setCorner] = useState(readCorner);
    const [dragPos, setDragPos] = useState(null);
    const dragStartRef = useRef(null);
    const latestPosRef = useRef(null);

    const onPressStart = (e) => {
        const p = e.touches ? e.touches[0] : e;
        const start = { x: p.clientX, y: p.clientY, time: Date.now() };
        dragStartRef.current = start;
        latestPosRef.current = start;
        setDragPos({ x: p.clientX, y: p.clientY });
    };

    useEffect(() => {
        if (!dragPos) return;
        const onMove = (e) => {
            const p = e.touches ? e.touches[0] : e;
            latestPosRef.current = { x: p.clientX, y: p.clientY };
            setDragPos({ x: p.clientX, y: p.clientY });
            if (e.cancelable && e.touches) e.preventDefault();
        };
        const onUp = () => {
            const start = dragStartRef.current;
            const last = latestPosRef.current;
            const dt = Date.now() - start.time;
            const dx = last.x - start.x;
            const dy = last.y - start.y;
            const movedFar = Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
            setDragPos(null);
            if (!movedFar && dt < CLICK_MAX_DURATION_MS) {
                handleOpen();
            } else {
                const newCorner = nearestCorner(last.x, last.y);
                setCorner(newCorner);
                writeCorner(newCorner);
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        window.addEventListener('touchcancel', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
        };
    }, [dragPos !== null]);

    const isDragging = !!dragPos;
    const isHidden = hidden && !isDragging;
    const isRightSide = corner.endsWith('r');
    const isMobile = typeof window !== 'undefined'
        && window.matchMedia && window.matchMedia('(max-width: 1023px)').matches;
    const size = isMobile ? FAB_SIZE_MOBILE : FAB_SIZE_DESKTOP;
    const half = size / 2;

    let positionStyle;
    if (isDragging) {
        positionStyle = { left: dragPos.x - half, top: dragPos.y - half };
    } else {
        const bot = isMobile ? MARGIN_BOTTOM_MOBILE : MARGIN_BOTTOM_DESKTOP;
        positionStyle = corner === 'br' ? { bottom: bot, right: MARGIN_X }
            : corner === 'bl' ? { bottom: bot, left: MARGIN_X }
            : corner === 'tr' ? { top: MARGIN_TOP, right: MARGIN_X }
            : { top: MARGIN_TOP, left: MARGIN_X };
    }

    let transform = '';
    if (isHidden) transform = isRightSide ? 'translateX(140%)' : 'translateX(-140%)';

    return (
        <>
        <style>{`
          .wa-fab {
            transition: top 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        bottom 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        left 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        right 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                        box-shadow 0.2s,
                        transform 0.25s ease;
            touch-action: none;
            user-select: none;
            -webkit-user-select: none;
          }
          .wa-fab.is-dragging {
            transition: box-shadow 0.2s, transform 0.1s ease !important;
            cursor: grabbing !important;
          }
          .wa-fab.is-hidden { pointer-events: none; }
          .wa-fab:hover:not(.is-dragging) { transform: scale(1.06); }
          @media (max-width: 1023px) {
            .wa-fab { width: 48px !important; height: 48px !important; }
            .wa-fab svg { width: 30px !important; height: 30px !important; }
          }
        `}</style>
        <button
            onMouseDown={onPressStart}
            onTouchStart={onPressStart}
            title="Contáctanos por WhatsApp — arrástrame para moverme a otra esquina"
            aria-label="Contáctanos por WhatsApp"
            aria-hidden={isHidden}
            tabIndex={isHidden ? -1 : 0}
            className={`wa-fab${isHidden ? ' is-hidden' : ''}${isDragging ? ' is-dragging' : ''}`}
            style={{
                position: 'fixed',
                zIndex: 9999,
                width: `${FAB_SIZE_DESKTOP}px`,
                height: `${FAB_SIZE_DESKTOP}px`,
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: isDragging ? 'grabbing' : 'grab',
                background: '#25D366',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isDragging
                    ? '0 12px 32px rgba(0,0,0,0.45), 0 0 0 2px rgba(37,211,102,0.5)'
                    : '0 4px 16px rgba(0,0,0,0.35)',
                transform,
                ...positionStyle,
            }}
        >
            <svg width="38" height="38" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ pointerEvents: 'none' }}>
                <path d="M16.001 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.259.593 4.46 1.72 6.404L3.2 28.8l6.552-1.68a12.75 12.75 0 006.249 1.633h.005c7.065 0 12.8-5.73 12.8-12.8 0-3.42-1.331-6.633-3.75-9.05A12.706 12.706 0 0016.001 3.2zm0 23.446a10.63 10.63 0 01-5.42-1.485l-.389-.23-4.032 1.034 1.075-3.926-.253-.404A10.6 10.6 0 015.334 16c0-5.876 4.783-10.658 10.667-10.658 2.848 0 5.524 1.108 7.536 3.12a10.585 10.585 0 013.131 7.542c0 5.876-4.782 10.642-10.667 10.642zm5.844-7.98c-.32-.16-1.895-.933-2.188-1.04-.294-.107-.507-.16-.72.16-.213.32-.826 1.04-1.013 1.253-.187.213-.373.24-.693.08-.32-.16-1.353-.499-2.577-1.591-.953-.85-1.596-1.9-1.782-2.22-.187-.32-.02-.493.14-.652.144-.144.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.734-.986-2.373-.26-.624-.524-.54-.72-.55l-.613-.011c-.213 0-.56.08-.853.4s-1.12 1.093-1.12 2.666c0 1.573 1.147 3.094 1.307 3.307.16.213 2.257 3.443 5.464 4.828.764.33 1.36.527 1.824.674.767.244 1.464.21 2.016.128.615-.092 1.895-.774 2.163-1.522.267-.749.267-1.39.187-1.522-.08-.133-.293-.213-.613-.373z" fill="#fff"/>
            </svg>
        </button>
        </>
    );
}
